import { getCurrentCanSign, getCurrentPermissions, getCurrentPosition, getCurrentRole, type TokenPosition } from "./auth";
import { canonicalRole } from "./roles";

/**
 * สิทธิ์ระดับจอ (ADR-0030) — อ่านจาก claim `perms` ของ access token เป็นหลัก
 *
 * ใช้ซ่อน/แสดงส่วนของจอเท่านั้น server บังคับซ้ำเสมอ (`Actor.Has`) — สิทธิ์ที่ผู้ดูแลเปลี่ยนมีผลกับจอเมื่อ
 * token รอบถัดไป (≤ 15 นาที) เหมือนฝั่ง server
 *
 * **ทางถอย** เมื่อ token ไม่มี claim `perms` (ออกก่อน ADR-0030): ใช้ชุดตั้งต้นของตำแหน่งที่ได้จาก role +
 * can_sign (กฎ backfill เดียวกับ `domain.DefaultPositionFor`) เฉพาะรหัสที่จอนี้ถามจริง — `dev-admin`
 * (โหมด memory) ถือว่ามีทุกสิทธิ์
 */

/** รหัสที่ web-admin ใช้ตัดสินจอ — ดูตาราง gate ใน ADR-0030 */
export const PERM = {
  claim: "APP-06",
  fullRequest: "REV-01",
  approve: "REV-02",
  returnForChanges: "REV-03",
  reject: "REV-04",
  sign: "REV-06",
  fullPII: "SEC-01",
  breakGlassRequest: "SEC-04",
  breakGlassApprove: "SEC-05",
  financeTasaeng: "DSH-04",
  financeDistrict: "DSH-07",
  financeProvince: "DSH-10",
  consultations: "SUP-01",
  incidents: "SUP-02",
  feeDecide: "CFG-02",
  manageAccounts: "CFG-11",
  positionMatrix: "CFG-12",
  userOverrides: "CFG-13",
  /** บันทึก/ยกเลิกรายจ่ายของตาแสงตัวเอง — FIN-06 ການເງິນຕາແສງ · FIN-07 ຫົວໜ້າຕາແສງ (ADR-0034) */
  expenseRecord: ["FIN-06", "FIN-07"],
  /** ดูรายงานการเงิน/รายการรายจ่ายโดยไม่มีสิทธิ์ Dashboard ການເງິນ */
  financeLedger: "FIN-08",
} as const;

export const FINANCE_PERMS = [PERM.financeTasaeng, PERM.financeDistrict, PERM.financeProvince] as const;

/** จอ ລາຍງານການເງິນ / ລາຍຈ່າຍ — สิทธิ์เดียวกับ `GET /v1/admin/reports/finance` (DSH-04/07/10 หรือ FIN-08) */
export const FINANCE_REPORT_PERMS = [...FINANCE_PERMS, PERM.financeLedger] as const;

/**
 * ชุดตั้งต้นของแต่ละตำแหน่ง (ช่อง `on` ของ migration 000048) เฉพาะรหัสใน `PERM` — ใช้เมื่อ token ไม่มี `perms`
 * เท่านั้น (+ ช่องที่ migration 000050 เปิด: SEC-04 ของ DO/AD, SEC-05 ของ PO — ADR-0032) ถ้าแก้ seed ของ matrix ต้องแก้ตรงนี้ด้วย (token เก่าหมดอายุใน 15 นาทีจึงเป็นช่วงสั้น ๆ)
 */
const FALLBACK: Partial<Record<TokenPosition, readonly string[]>> = {
  VH: ["APP-06", "REV-01", "REV-03", "REV-04", "SEC-01"],
  TH: ["APP-06", "CFG-11", "DSH-04", "FIN-07", "FIN-08", "REV-01", "REV-02", "REV-03", "REV-04", "REV-06", "SEC-01", "SUP-01", "SUP-02"],
  TK: ["APP-06", "REV-01", "REV-02", "REV-03", "REV-04", "SEC-01", "SUP-01", "SUP-02"],
  TR: ["SEC-01", "SUP-01", "SUP-02"],
  TF: ["DSH-04", "FIN-06", "FIN-08"],
  DO: ["CFG-11", "DSH-07", "FIN-08", "SEC-04"],
  PO: ["CFG-02", "CFG-11", "DSH-10", "FIN-08", "SEC-05"],
  AD: ["CFG-11", "CFG-13", "DSH-04", "DSH-07", "DSH-10", "SEC-04"],
};

/** ตำแหน่งตามกฎ backfill (role + can_sign) — ใช้เมื่อ token ไม่มี claim `pos` */
function backfillPosition(): TokenPosition | null {
  const role = getCurrentRole();
  if (!role) return null;
  switch (canonicalRole(role)) {
    case "citizen":
      return "CIT";
    case "village_head":
      return "VH";
    case "officer":
      return getCurrentCanSign() ? "TH" : "TK";
    case "district_finance":
      return "DO";
    case "province_oversight":
      return "PO";
    case "super_admin":
      return "SA";
  }
}

/** ตำแหน่งของผู้ใช้ปัจจุบัน — claim `pos` ก่อน ไม่มีก็ถอยตามกฎ backfill */
export function currentPosition(): TokenPosition | null {
  return getCurrentPosition() ?? backfillPosition();
}

/** มีสิทธิ์รหัสนี้ไหม (หรือรหัสใดรหัสหนึ่งในรายการ) */
export function hasPerm(code: string | readonly string[]): boolean {
  const codes = typeof code === "string" ? [code] : code;
  const perms = getCurrentPermissions();
  if (perms !== null) return codes.some((c) => perms.includes(c));
  const pos = currentPosition();
  if (pos === null) return false;
  if (pos === "SA") return true;
  const set = FALLBACK[pos] ?? [];
  return codes.some((c) => set.includes(c));
}

/** DSH-04/07/10 — Dashboard ການເງິນ ຂັ້ນຕາແສງ/ເມືອງ/ແຂວງ (`Actor.SeesFinanceStats`) */
export function seesFinanceStats(): boolean {
  return hasPerm(FINANCE_PERMS);
}

/** SEC-01 **และ** REV-01 — เห็นข้อมูลผู้ขอเต็ม (`Actor.SeesApplicantPII`); คนอื่นเห็นแบบบัง */
export function seesApplicantPII(): boolean {
  return hasPerm(PERM.fullPII) && hasPerm(PERM.fullRequest);
}

/** APP-06 — รับคำร้องเข้าคิว (`submitted → in_review`) */
export function canClaimRequests(): boolean {
  return hasPerm(PERM.claim);
}

/** REV-02/03/04 อย่างใดอย่างหนึ่ง — `Actor.CanReviewRequests()` */
export function canReviewRequests(): boolean {
  return hasPerm([PERM.approve, PERM.returnForChanges, PERM.reject]);
}

/** REV-06 คู่กับ claim `can_sign` — ตรงกับที่ server เช็คตอนเซ็น (`User.CanSignDocuments` + REV-06) */
export function canSignDocuments(): boolean {
  return hasPerm(PERM.sign) && getCurrentCanSign();
}

/** SUP-01 — อ่าน/จัดการเนื้อหานัดปรึกษา (คนอื่นเห็นแค่สถิติ) */
export function canManageConsultations(): boolean {
  return hasPerm(PERM.consultations);
}

/** CFG-11 — เปิด/แก้/ปิดบัญชีตำแหน่งที่ต่ำกว่า */
export function canManageAccounts(): boolean {
  return hasPerm(PERM.manageAccounts);
}
